/**
 * Secret scan before Apiboo commits a workspace (git sync Stage 1, card 5).
 *
 * Values of variables marked secret never reach a tracked file (they live in
 * `*.local.yaml`). Everything else can: a bearer token typed straight into a
 * request's auth, a password in a header, an API key in a variable nobody
 * ticked "secret" (git-sync audit 01.10.2026). This scans what is ABOUT to be
 * committed and blocks the commit with `file:line` and a rule — never the
 * value.
 *
 * It reuses the AI path's narrow heuristics (services/ai/secretHeuristics.js):
 * vendor key formats, JWTs, private keys, URLs with inline credentials, and a
 * credential-named field holding a long, non-template value. So `{{token}}`,
 * the word "token" in a description and short placeholders pass.
 */
import { parseYamlFile } from '../../utils/yamlWorkspace/deserialize.js';
import { looksSecret } from '../ai/secretHeuristics.js';
import { scanTextForSecrets } from './secretScanText.js';

/** Never scanned: untracked by the generated .gitignore, or not data. */
const isScanned = (relPath) => relPath.endsWith('.yaml') && !relPath.endsWith('.local.yaml');
/** Free text (documents, tasks — git sync Stage 2): scanned line by line. */
const isText = (relPath) => relPath.endsWith('.md');

/** First 1-based line of `text` that contains `needle`, else 1. */
function lineOf(text, needle, from = 0) {
  const at = text.indexOf(needle, from);
  if (at < 0) return 1;
  return text.slice(0, at).split('\n').length;
}

function walk(node, parentKey, visit) {
  if (Array.isArray(node)) {
    node.forEach((child) => walk(child, parentKey, visit));
    return;
  }
  if (!node || typeof node !== 'object') return;
  // A `{ key, value }` row (variables, headers, params, form fields): judge the
  // VALUE by the row's own key name — they sit on different lines.
  if (typeof node.key === 'string' && typeof node.value === 'string') {
    visit(node.key, node.value);
  }
  for (const [k, v] of Object.entries(node)) {
    if (typeof v === 'string') {
      if (k !== 'value') visit(k, v);
    } else {
      walk(v, k, visit);
    }
  }
}

/**
 * @param {Record<string, string>} files relative path → file text (what will be committed)
 * @returns {{ file: string, line: number, rule: string }[]}
 */
export function scanForSecrets(files) {
  const findings = [];
  for (const [file, text] of Object.entries(files || {})) {
    if (typeof text !== 'string') continue;
    if (isText(file)) {
      for (const f of scanTextForSecrets(text)) findings.push({ file, ...f });
      continue;
    }
    if (!isScanned(file)) continue;
    let doc;
    try {
      doc = parseYamlFile(text);
    } catch {
      continue; // unreadable files are reported by the loader, not here
    }
    const seen = new Set();
    walk(doc, null, (key, value) => {
      if (!looksSecret(key, value)) return;
      const line = lineOf(text, value.split('\n')[0]);
      const id = `${line}:${key}`;
      if (seen.has(id)) return;
      seen.add(id);
      findings.push({ file, line, rule: `looks like a credential in "${key}"` });
    });
  }
  return findings.sort((a, b) => a.file.localeCompare(b.file) || a.line - b.line);
}

/** One-line summary for a notification: `file:line — rule`, the first few. */
export function describeFindings(findings, max = 3) {
  const shown = findings.slice(0, max).map((f) => `${f.file}:${f.line} — ${f.rule}`);
  const more = findings.length > max ? ` (+${findings.length - max} more)` : '';
  return shown.join('; ') + more;
}
